import { BaseFeature } from '../../core/base-feature.js';

/**
 * FileBrowserFeature - Remote file system browsing (Frontend)
 * Handles: listing drives, browsing directories, previewing files, downloading
 */
export class FileBrowserFeature extends BaseFeature {
    constructor(coreAPI) {
        super(coreAPI);

        // State
        this.drives = [];
        this.currentPath = null;
        this.currentItems = [];
        this.selectedFile = null;
        this.isLoading = false;
        this.currentPathIsBlocked = false;
        this.currentPathBlockInfo = null;

        // Cache
        this.directoryCache = new Map();
        this.cacheExpiry = 30000; // 30 seconds

        // Thumbnail queue + cache + dedup
        this._thumbnailQueue = [];        // { path, resolve, reject }
        this._activeCount = 0;            // in-flight requests (max 4)
        this._inflightMap = new Map();    // path → Promise (dedup)
        this._thumbnailCache = new Map(); // path → { dataUri, timestamp } (LRU, max 500)
        this._maxCacheSize = 500;
        this._drainScheduled = false;

        // Event listeners for React components
        this.updateCallbacks = new Set();

        // Pending promises for request/response
        this._pendingRequests = new Map();
    }

    static getMetadata() {
        return {
            id: 'file-browser',
            name: 'file-browser',
            version: 'v1.1',
            capabilities: [
                'list_drives',
                'list_directory',
                'get_file_info',
                'get_preview',
                'get_preview_batch',
                'download_file',
                'upload_file',
                'launch_file',
                'search_files',
                'stream_video',
                'stream_stop'
            ],
            dependencies: []
        };
    }

    async initialize() {
        console.log('[FileBrowser] Initializing...');
        console.log('[FileBrowser] Initialized');
    }

    async handleMessage(message) {
        const { action, payload, messageId, fromId } = message;

        switch (action) {
            case 'list_drives_response':
                return this.handleListDrivesResponse(payload);

            case 'list_directory_response':
                return this.handleListDirectoryResponse(payload);

            case 'get_file_info_response':
                return this.handleGetFileInfoResponse(payload);

            case 'get_preview_response':
                return this.handleGetPreviewResponse(payload);

            case 'download_file_response':
                return this.handleDownloadFileResponse(payload);

            case 'upload_file_response':
                return this.handleUploadFileResponse(payload);

            case 'launch_file_response':
                return this.handleLaunchFileResponse(payload);

            case 'search_files_response':
                return this.handleSearchFilesResponse(payload);

            case 'stream_video_response':
                return this.handleStreamVideoResponse(payload);

            case 'stream_stop_response':
                return this.handleStreamStopResponse(payload);

            case 'get_preview_batch_response':
                return this.handleGetPreviewBatchResponse(payload);

            default:
                console.warn(`[FileBrowser] Unknown action: ${action}`);
        }
    }

    // =====================================
    // Drive Operations
    // =====================================

    /**
     * Request list of drives
     * @returns {Promise<Object>} Result with drives array
     */
    async listDrives() {
        this.isLoading = true;
        this.notifyUpdateListeners();
        console.log('[FileBrowser] Requesting drives...');

        return new Promise((resolve) => {
            this.sendMessage('list_drives', {});
            this._pendingRequests.set('list_drives', resolve);
        });
    }

    handleListDrivesResponse(payload) {
        this.isLoading = false;

        const resolve = this._pendingRequests.get('list_drives');
        if (resolve) {
            this._pendingRequests.delete('list_drives');
            resolve(payload);
        }

        if (!payload.success) {
            console.error('[FileBrowser] Failed to get drives:', payload.error);
            this.notifyUpdateListeners();
            return;
        }

        this.drives = payload.drives || [];
        console.log(`[FileBrowser] Loaded ${this.drives.length} drives`);
        this.notifyUpdateListeners();
    }

    // =====================================
    // Directory Operations
    // =====================================

    /**
     * List contents of a directory
     * @param {string} dirPath - Directory path
     * @param {Object} options - Options (showHidden, sortBy, sortDir, limit)
     * @returns {Promise<Object>} Result with items array
     */
    async listDirectory(dirPath, options = {}) {
        this.isLoading = true;
        this.notifyUpdateListeners();
        console.log(`[FileBrowser] Listing directory: ${dirPath}`);

        return new Promise((resolve) => {
            this.sendMessage('list_directory', { path: dirPath, options });
            this._pendingRequests.set('list_directory', resolve);
        });
    }

    handleListDirectoryResponse(payload) {
        this.isLoading = false;

        const resolve = this._pendingRequests.get('list_directory');
        if (resolve) {
            this._pendingRequests.delete('list_directory');
            resolve(payload);
        }

        if (!payload.success) {
            console.error('[FileBrowser] Failed to list directory:', payload.error);
            this.notifyUpdateListeners();
            return;
        }

        this.currentPath = payload.path;
        this.currentItems = payload.items || [];
        this.selectedFile = null;
        this.currentPathIsBlocked = payload.isBlocked || false;
        this.currentPathBlockInfo = payload.blockInfo || null;

        // Cache the result
        this.directoryCache.set(payload.path, {
            data: payload,
            timestamp: Date.now()
        });

        console.log(`[FileBrowser] Listed ${this.currentItems.length} items in ${payload.path}${payload.isBlocked ? ' [BLOCKED FOLDER]' : ''}`);
        this.notifyUpdateListeners();
    }

    /**
     * Navigate to parent directory
     */
    async navigateUp() {
        if (!this.currentPath) return;

        // Get parent path
        const parts = this.currentPath.split(/[/\\]/);
        parts.pop();
        const parentPath = parts.join('\\') || parts[0] + '\\';

        if (parentPath && parentPath !== this.currentPath) {
            return this.listDirectory(parentPath);
        }
    }

    /**
     * Navigate to a drive
     * @param {string} driveLetter - Drive letter (e.g., 'C:')
     */
    async navigateToDrive(driveLetter) {
        return this.listDirectory(driveLetter + '\\');
    }

    // =====================================
    // File Operations
    // =====================================

    /**
     * Get detailed file information
     * @param {string} filePath - File path
     * @returns {Promise<Object>} Result with file info
     */
    async getFileInfo(filePath) {
        console.log(`[FileBrowser] Getting file info: ${filePath}`);

        return new Promise((resolve) => {
            this.sendMessage('get_file_info', { path: filePath });
            this._pendingRequests.set('get_file_info', resolve);
        });
    }

    handleGetFileInfoResponse(payload) {
        const resolve = this._pendingRequests.get('get_file_info');
        if (resolve) {
            this._pendingRequests.delete('get_file_info');
            resolve(payload);
        }

        if (payload.success && payload.file) {
            this.selectedFile = payload.file;
            this.notifyUpdateListeners();
        }
    }

    /**
     * Get file preview (image or text)
     * @param {string} filePath - File path
     * @param {Object} options - Options (maxWidth, maxHeight, quality)
     * @returns {Promise<Object>} Result with preview data
     */
    async getPreview(filePath, options = {}) {
        console.log(`[FileBrowser] Getting preview: ${filePath}`);

        return new Promise((resolve) => {
            // Use path as unique key to handle concurrent requests
            const requestKey = `get_preview:${filePath}`;
            this.sendMessage('get_preview', { path: filePath, options });
            this._pendingRequests.set(requestKey, resolve);
        });
    }

    /**
     * Get a small thumbnail for grid view (queued, cached, deduped)
     * @param {string} filePath - File path
     * @param {number} maxSize - Maximum dimension (default 200)
     * @returns {Promise<Object>} Result with thumbnail data
     */
    async getThumbnail(filePath, maxSize = 200) {
        // 1. Check in-memory cache
        const cached = this._thumbnailCache.get(filePath);
        if (cached) {
            // LRU: move to end
            this._thumbnailCache.delete(filePath);
            this._thumbnailCache.set(filePath, cached);
            return { success: true, thumbnail: cached.dataUri };
        }

        // 2. Dedup: if already in-flight, return the same promise
        if (this._inflightMap.has(filePath)) {
            return this._inflightMap.get(filePath);
        }

        // 3. Create promise and either fire immediately or queue
        const promise = new Promise((resolve, reject) => {
            if (this._activeCount < 4) {
                this._activeCount++;
                this._fireSingleThumbnail(filePath, maxSize, resolve, reject);
            } else {
                this._thumbnailQueue.push({ path: filePath, maxSize, resolve, reject });
            }
        });

        this._inflightMap.set(filePath, promise);
        return promise;
    }

    /**
     * Fire a single thumbnail request via get_preview
     */
    _fireSingleThumbnail(filePath, maxSize, resolve, reject) {
        this.getPreview(filePath, {
            maxWidth: maxSize,
            maxHeight: maxSize,
            quality: 0.7,
            thumbnail: true
        }).then(result => {
            const thumbResult = this._transformPreviewToThumbnail(result, filePath);
            resolve(thumbResult);
        }).catch(err => {
            resolve({ success: false });
        }).finally(() => {
            this._inflightMap.delete(filePath);
            this._activeCount--;
            this._scheduleDrain();
        });
    }

    /**
     * Transform a preview response into thumbnail format and cache it
     */
    _transformPreviewToThumbnail(result, filePath) {
        if (result?.success && result?.preview?.data) {
            const isImage = result.preview.type === 'image';
            const isVideo = result.preview.type === 'video' && result.preview.thumbnailAvailable;
            if (isImage || isVideo) {
                this._cacheThumbnail(filePath, result.preview.data);
                return { success: true, thumbnail: result.preview.data };
            }
        }
        return { success: false };
    }

    /**
     * Add a thumbnail to the LRU cache
     */
    _cacheThumbnail(filePath, dataUri) {
        // Evict oldest if at capacity
        if (this._thumbnailCache.size >= this._maxCacheSize) {
            const oldest = this._thumbnailCache.keys().next().value;
            this._thumbnailCache.delete(oldest);
        }
        this._thumbnailCache.set(filePath, { dataUri, timestamp: Date.now() });
    }

    /**
     * Schedule a drain of the thumbnail queue (batched)
     */
    _scheduleDrain() {
        if (this._drainScheduled) return;
        this._drainScheduled = true;
        // Microtask to coalesce multiple completions in the same tick
        Promise.resolve().then(() => {
            this._drainScheduled = false;
            this._drainQueue();
        });
    }

    /**
     * Drain queued thumbnails — batch up to 10 paths into a single message
     */
    _drainQueue() {
        if (this._thumbnailQueue.length === 0 || this._activeCount >= 4) return;

        // Collect up to 10 pending paths for a batch, counting as 1 active slot
        const batch = this._thumbnailQueue.splice(0, Math.min(this._thumbnailQueue.length, 10));
        if (batch.length === 0) return;

        this._activeCount++;

        const paths = batch.map(b => b.path);
        const resolvers = new Map(batch.map(b => [b.path, { resolve: b.resolve, reject: b.reject }]));
        const maxSize = batch[0].maxSize || 200;

        console.log(`[FileBrowser] Sending batch thumbnail request for ${paths.length} paths`);

        // Store resolvers for batch response
        const batchKey = `get_preview_batch:${Date.now()}:${Math.random()}`;
        this._pendingRequests.set(batchKey, { resolvers, paths });

        this.sendMessage('get_preview_batch', {
            paths,
            options: { maxWidth: maxSize, maxHeight: maxSize, quality: 0.7, thumbnail: true },
            batchKey
        });
    }

    /**
     * Handle batch preview response
     */
    handleGetPreviewBatchResponse(payload) {
        const { batchKey, results } = payload;

        const pending = this._pendingRequests.get(batchKey);
        if (!pending) {
            // Try to find by paths fallback
            console.warn('[FileBrowser] No pending batch request found for key:', batchKey);
            return;
        }

        this._pendingRequests.delete(batchKey);
        const { resolvers } = pending;

        // Resolve each individual promise
        for (const [filePath, resolver] of resolvers) {
            const result = results?.[filePath];
            const thumbResult = this._transformPreviewToThumbnail(result, filePath);
            this._inflightMap.delete(filePath);
            resolver.resolve(thumbResult);
        }

        this._activeCount--;
        this._scheduleDrain();
    }

    /**
     * Clear the thumbnail queue (called on directory navigation)
     * Rejects all queued items; in-flight ones finish but components are unmounted
     */
    clearThumbnailQueue() {
        const queueLen = this._thumbnailQueue.length;
        for (const item of this._thumbnailQueue) {
            this._inflightMap.delete(item.path);
            item.resolve({ success: false, cancelled: true });
        }
        this._thumbnailQueue = [];
        if (queueLen > 0) {
            console.log(`[FileBrowser] Cleared ${queueLen} queued thumbnail requests`);
        }
    }

    handleGetPreviewResponse(payload) {
        // Use path from response to find the correct pending request
        const requestKey = `get_preview:${payload.path}`;
        const resolve = this._pendingRequests.get(requestKey);
        if (resolve) {
            this._pendingRequests.delete(requestKey);
            resolve(payload);
        } else {
            // Fallback: try legacy key for backwards compatibility
            const legacyResolve = this._pendingRequests.get('get_preview');
            if (legacyResolve) {
                this._pendingRequests.delete('get_preview');
                legacyResolve(payload);
            } else {
                console.warn(`[FileBrowser] No pending request found for preview: ${payload.path}`);
            }
        }
    }

    /**
     * Download a file
     * @param {string} filePath - File path
     * @returns {Promise<Object>} Result with download data
     */
    async downloadFile(filePath) {
        console.log(`[FileBrowser] Downloading: ${filePath}`);

        return new Promise((resolve) => {
            this.sendMessage('download_file', { path: filePath });
            this._pendingRequests.set('download_file', resolve);
        });
    }

    handleDownloadFileResponse(payload) {
        const resolve = this._pendingRequests.get('download_file');
        if (resolve) {
            this._pendingRequests.delete('download_file');
            resolve(payload);
        }

        if (payload.success && payload.download) {
            // Trigger browser download
            this.triggerDownload(payload.download);
        }
    }

    /**
     * Upload a file to the remote desktop
     * @param {string} targetDir - Target directory path on remote
     * @param {string} fileName - Name for the uploaded file
     * @param {string} base64Data - File content as base64 string
     * @returns {Promise<Object>} Result { success, path, size, sizeFormatted }
     */
    async uploadFile(targetDir, fileName, base64Data) {
        console.log(`[FileBrowser] Uploading: ${fileName} to ${targetDir}`);

        return new Promise((resolve) => {
            this.sendMessage('upload_file', { targetDir, fileName, data: base64Data });
            this._pendingRequests.set('upload_file', resolve);
        });
    }

    handleUploadFileResponse(payload) {
        const resolve = this._pendingRequests.get('upload_file');
        if (resolve) {
            this._pendingRequests.delete('upload_file');
            resolve(payload);
        }
    }

    // =====================================
    // Launch/Execute
    // =====================================

    /**
     * Launch/open a file on the remote desktop with its default application
     * @param {string} filePath - File path to launch
     * @returns {Promise<Object>} Result { success, path }
     */
    async launchFile(filePath) {
        console.log(`[FileBrowser] Launching: ${filePath}`);

        return new Promise((resolve) => {
            this.sendMessage('launch_file', { path: filePath });
            this._pendingRequests.set('launch_file', resolve);
        });
    }

    handleLaunchFileResponse(payload) {
        const resolve = this._pendingRequests.get('launch_file');
        if (resolve) {
            this._pendingRequests.delete('launch_file');
            resolve(payload);
        }
    }

    // =====================================
    // Recursive Search
    // =====================================

    /**
     * Search files recursively from a root path
     * @param {string} rootPath - Directory to start searching from
     * @param {string} query - Search query (filename substring or content)
     * @param {Object} options - { maxResults, includeContent }
     * @returns {Promise<Object>} Result { success, results[], totalResults, truncated }
     */
    async searchFiles(rootPath, query, options = {}) {
        console.log(`[FileBrowser] Searching: "${query}" in ${rootPath}`);

        return new Promise((resolve) => {
            this.sendMessage('search_files', { rootPath, query, options });
            this._pendingRequests.set('search_files', resolve);
        });
    }

    handleSearchFilesResponse(payload) {
        const resolve = this._pendingRequests.get('search_files');
        if (resolve) {
            this._pendingRequests.delete('search_files');
            resolve(payload);
        }
    }

    // =====================================
    // Video Streaming
    // =====================================

    /**
     * Start streaming a video file
     * @param {string} filePath - Path to the video file
     * @returns {Promise<Object>} Stream info { streamId, streamIdHash, mimeType, fileSize, totalChunks }
     */
    async streamVideo(filePath) {
        console.log(`[FileBrowser] Starting video stream: ${filePath}`);

        return new Promise((resolve) => {
            this.sendMessage('stream_video', { path: filePath });
            this._pendingRequests.set('stream_video', resolve);
        });
    }

    handleStreamVideoResponse(payload) {
        const resolve = this._pendingRequests.get('stream_video');
        if (resolve) {
            this._pendingRequests.delete('stream_video');
            resolve(payload);
        }

        if (payload.success) {
            console.log(`[FileBrowser] Video stream started: ${payload.streamId} (${payload.totalChunks} chunks)`);
        }
    }

    /**
     * Stop a video stream
     * @param {string} streamId - Stream ID to stop
     * @returns {Promise<Object>} Result { success }
     */
    async stopVideo(streamId) {
        console.log(`[FileBrowser] Stopping video stream: ${streamId}`);

        return new Promise((resolve) => {
            this.sendMessage('stream_stop', { streamId });
            this._pendingRequests.set('stream_stop', resolve);
        });
    }

    handleStreamStopResponse(payload) {
        const resolve = this._pendingRequests.get('stream_stop');
        if (resolve) {
            this._pendingRequests.delete('stream_stop');
            resolve(payload);
        }

        if (payload.success) {
            console.log(`[FileBrowser] Video stream stopped`);
        }
    }

    /**
     * Trigger browser download for file data
     * @param {Object} download - Download data { name, data, mimeType }
     */
    triggerDownload(download) {
        const { name, data, mimeType } = download;

        // Convert base64 to blob
        const byteCharacters = atob(data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: mimeType });

        // Create download link
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        console.log(`[FileBrowser] Download triggered: ${name}`);
    }

    // =====================================
    // Selection
    // =====================================

    /**
     * Select a file
     * @param {Object} file - File object from items list
     */
    selectFile(file) {
        this.selectedFile = file;
        this.notifyUpdateListeners();
    }

    /**
     * Clear selection
     */
    clearSelection() {
        this.selectedFile = null;
        this.notifyUpdateListeners();
    }

    // =====================================
    // Cache Operations
    // =====================================

    /**
     * Get cached directory listing
     * @param {string} dirPath - Directory path
     * @returns {Object|null} Cached result or null
     */
    getCachedDirectory(dirPath) {
        const cached = this.directoryCache.get(dirPath);
        if (cached && Date.now() - cached.timestamp < this.cacheExpiry) {
            return cached.data;
        }
        return null;
    }

    /**
     * Clear all cached data
     */
    clearCache() {
        this.directoryCache.clear();
        this._thumbnailCache.clear();
        console.log('[FileBrowser] Cache cleared');
    }

    /**
     * Invalidate cache for a specific path
     * @param {string} dirPath - Path to invalidate
     */
    invalidateCache(dirPath) {
        this.directoryCache.delete(dirPath);
    }

    // =====================================
    // Event Listeners (for React components)
    // =====================================

    /**
     * Subscribe to state updates
     * @param {Function} callback - Called when state changes
     * @returns {Function} Unsubscribe function
     */
    onUpdate(callback) {
        this.updateCallbacks.add(callback);
        return () => this.updateCallbacks.delete(callback);
    }

    notifyUpdateListeners() {
        const data = {
            drives: this.drives,
            currentPath: this.currentPath,
            items: this.currentItems,
            selectedFile: this.selectedFile,
            isLoading: this.isLoading,
            currentPathIsBlocked: this.currentPathIsBlocked,
            currentPathBlockInfo: this.currentPathBlockInfo
        };

        for (const callback of this.updateCallbacks) {
            try {
                callback(data);
            } catch (error) {
                console.error('[FileBrowser] Error in update callback:', error);
            }
        }
    }

    // =====================================
    // Getters
    // =====================================

    getDrives() {
        return this.drives;
    }

    getCurrentPath() {
        return this.currentPath;
    }

    getCurrentItems() {
        return this.currentItems;
    }

    getSelectedFile() {
        return this.selectedFile;
    }

    getIsLoading() {
        return this.isLoading;
    }

    getCurrentPathIsBlocked() {
        return this.currentPathIsBlocked;
    }

    getCurrentPathBlockInfo() {
        return this.currentPathBlockInfo;
    }

    // =====================================
    // Cleanup
    // =====================================

    async cleanup() {
        console.log('[FileBrowser] Cleaning up...');
        this.drives = [];
        this.currentPath = null;
        this.currentItems = [];
        this.selectedFile = null;
        this.currentPathIsBlocked = false;
        this.currentPathBlockInfo = null;
        this.directoryCache.clear();
        this.clearThumbnailQueue();
        this._thumbnailCache.clear();
        this._inflightMap.clear();
        this._activeCount = 0;
        this.updateCallbacks.clear();
        this._pendingRequests.clear();
    }
}
